/**
 * useGuestbook — 방명록 API 훅
 *
 * 방문자 공개 목록 (cursor pagination), 작성, 소유자 관리 훅 제공.
 */

import { useState, useCallback } from 'react'
import { useApiFetch, useApiMutation } from './useApiFetch.js'
import type {
  GuestbookListPublic,
  GuestbookListOwner,
  GuestbookEntryPublic,
  SubmitGuestbookEntry,
  PatchGuestbookEntry,
  DeleteGuestbookByPassword,
} from '@cugumi/shared/schemas'
import { apiFetch, getErrorMessage } from '@/lib/api.js'

// ---------------------------------------------------------------------------
// 방문자 공개 목록 (cursor pagination — 수동 관리)
// ---------------------------------------------------------------------------

interface UseGuestbookPublicResult {
  entries: GuestbookEntryPublic[]
  total: number
  hasMore: boolean
  isLoading: boolean
  error: string | null
  loadMore: () => Promise<void>
  /** 낙관적으로 항목 추가 (방금 작성한 글) */
  addOptimistic: (entry: GuestbookEntryPublic) => void
}

const PAGE_SIZE = 7

export function useGuestbookPublic(invitationId: string): UseGuestbookPublicResult {
  const basePath = `/api/invitations/${invitationId}/guestbook`
  const path = `${basePath}?limit=${PAGE_SIZE}`
  const { data, isLoading, error } = useApiFetch<GuestbookListPublic>(path, { skipAuth: true })

  const [extraEntries, setExtraEntries] = useState<GuestbookEntryPublic[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [optimisticEntries, setOptimisticEntries] = useState<GuestbookEntryPublic[]>([])

  // 첫 페이지 로드 완료 시 커서 설정
  const firstCursor = data?.nextCursor ?? null
  const effectiveCursor = cursor ?? firstCursor

  const allEntries = [...optimisticEntries, ...(data?.entries ?? []), ...extraEntries]

  const loadMore = useCallback(async () => {
    if (!effectiveCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const res = await apiFetch<GuestbookListPublic>(
        `${basePath}?limit=${PAGE_SIZE}&cursor=${encodeURIComponent(effectiveCursor)}`,
        { skipAuth: true },
      )
      if (res.success) {
        setExtraEntries((prev) => [...prev, ...res.data.entries])
        setCursor(res.data.nextCursor)
      }
    } finally {
      setLoadingMore(false)
    }
  }, [effectiveCursor, loadingMore, path])

  const hasMore = cursor !== undefined
    ? cursor !== null
    : (data?.nextCursor ?? null) !== null

  const addOptimistic = useCallback((entry: GuestbookEntryPublic) => {
    setOptimisticEntries((prev) => [entry, ...prev])
  }, [])

  return {
    entries: allEntries,
    total: (data?.total ?? 0) + optimisticEntries.length,
    hasMore,
    isLoading: isLoading || loadingMore,
    error,
    loadMore,
    addOptimistic,
  }
}

// ---------------------------------------------------------------------------
// 방명록 작성
// ---------------------------------------------------------------------------

interface UseGuestbookSubmitResult {
  submit: (body: SubmitGuestbookEntry) => Promise<{ id: string; createdAt: string } | null>
  isLoading: boolean
  error: string | null
}

export function useGuestbookSubmit(invitationId: string): UseGuestbookSubmitResult {
  const { trigger, isLoading, error } = useApiMutation<
    { id: string; createdAt: string },
    SubmitGuestbookEntry
  >(`/api/invitations/${invitationId}/guestbook`, 'POST')

  const submit = useCallback(
    async (body: SubmitGuestbookEntry) => trigger(body),
    [trigger],
  )

  return { submit, isLoading, error }
}

// ---------------------------------------------------------------------------
// 소유자 전체 목록
// ---------------------------------------------------------------------------

export function useGuestbookOwner(invitationId: string) {
  const path = `/api/invitations/${invitationId}/guestbook`
  const { data, isLoading, error, refetch } = useApiFetch<GuestbookListOwner>(path)

  return {
    entries: data?.entries ?? [],
    total: data?.total ?? 0,
    hiddenCount: data?.hiddenCount ?? 0,
    privateCount: data?.privateCount ?? 0,
    isLoading,
    error,
    refetch,
  }
}

// ---------------------------------------------------------------------------
// 숨김 토글
// ---------------------------------------------------------------------------

export function useGuestbookHide(invitationId: string) {
  const { trigger, isLoading } = useApiMutation<
    { message: string },
    PatchGuestbookEntry
  >(`/api/invitations/${invitationId}/guestbook`, 'PATCH', {
    invalidateKeys: [[`/api/invitations/${invitationId}/guestbook`]],
  })

  const toggle = useCallback(
    (entryId: string, isHidden: boolean) =>
      trigger({ isHidden }, { pathOverride: `/api/invitations/${invitationId}/guestbook/${entryId}` }),
    [trigger, invitationId],
  )

  return { toggle, isLoading }
}

// ---------------------------------------------------------------------------
// 삭제
// ---------------------------------------------------------------------------

export function useGuestbookDelete(invitationId: string) {
  const { trigger, isLoading } = useApiMutation<{ message: string }>(
    `/api/invitations/${invitationId}/guestbook`,
    'DELETE',
    {
      invalidateKeys: [[`/api/invitations/${invitationId}/guestbook`]],
    },
  )

  const remove = useCallback(
    (entryId: string) =>
      trigger(undefined, { pathOverride: `/api/invitations/${invitationId}/guestbook/${entryId}` }),
    [trigger, invitationId],
  )

  return { remove, isLoading }
}

// ---------------------------------------------------------------------------
// 작성자 비밀번호 삭제 (public)
// ---------------------------------------------------------------------------

export function useGuestbookDeleteByPassword(invitationId: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const remove = useCallback(
    async (entryId: string, password: string): Promise<boolean> => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await apiFetch<{ message: string }>(
          `/api/invitations/${invitationId}/guestbook/${entryId}/delete`,
          {
            method: 'POST',
            body: JSON.stringify({ password }),
            skipAuth: true,
          },
        )
        if (res.success) return true
        setError(getErrorMessage(res.error))
        return false
      } catch {
        setError('네트워크 오류가 발생했습니다')
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [invitationId],
  )

  return { remove, isLoading, error }
}
